import { useEffect } from "react";

import { Button, Typography } from "@mui/material";
import classNames from "classnames";
import { useForm, useWatch } from "react-hook-form";
import { Link } from "react-router-dom";

import { useProfile, useProfileInit } from "../../../../../hooks/useProfile";
import { CommonInput } from "../../../../common";
import { ProfileAvatar } from "./ProfileAvatar";

import "./profile.scss";

export type TProfileFieldValues = {
	nickName: string;
	languages: string;
	introduction: string;
	avatar: string;
};

export function Profile() {
	const { profile, updateProfile, deleteProfilePicture } = useProfile();
	useProfileInit();

	const { handleSubmit, control, reset, setValue } =
		useForm<TProfileFieldValues>({
			defaultValues: {
				nickName: "",
				languages: "",
				introduction: "",
				avatar: "",
			},
			mode: "all",
		});

	const { nickName, languages, introduction, avatar } = useWatch({ control });

	useEffect(() => {
		if (profile) {
			reset({
				nickName: profile.nickName,
				languages: profile.languages,
				introduction: profile.introduction,
				avatar: profile.url,
			});
		}
	}, [profile, reset]);

	const onClickSave = (data: TProfileFieldValues) => {
		const { avatar: newAvatar, ...others } = data;
		if (newAvatar === "") {
			deleteProfilePicture();
		}
		updateProfile(others);
	};

	const onClickCancel = () => {
		if (profile) {
			reset({
				nickName: profile.nickName,
				languages: profile.languages,
				introduction: profile.introduction,
				avatar: profile.url,
			});
		}
	};

	const isDisabled =
		nickName === profile?.nickName &&
		languages === profile?.languages &&
		introduction === profile?.introduction &&
		avatar === profile?.url;

	return (
		<div className="right-column-content-container">
			<div className="account-profile-container">
				<div className="title-container">
					<Typography className="small-title-text bold title">
						Public Profile
					</Typography>
					<Link to={`/profile/${profile?.profileSlug}`}>
						<Button className="normal-button black-button no-transform">
							View Profile
						</Button>
					</Link>
				</div>
				<Typography className="normal-text item-separator-tiny">
					This information will be used to help students and instructors get to
					know you.
				</Typography>
				<form
					className="profile-form item-separator-small"
					onSubmit={handleSubmit(onClickSave)}>
					<div className="first-column">
						<ProfileAvatar setValue={setValue} control={control} />
						{/* <div className="verification-container">
							<Typography className="big-text bold">Verification</Typography>
							<div className="verification-item">
								{profile.identity && (
									<FaCheck className="normal-icon check-icon" />
								)}
								{!profile.identity && (
									<IoClose className="normal-icon close-icon" />
								)}
								<Typography className="normal-text">Identity</Typography>
							</div>
							<div className="verification-item">
								{profile.certificates && (
									<FaCheck className="normal-icon check-icon" />
								)}
								{!profile.certificates && (
									<IoClose className="normal-icon close-icon" />
								)}
								<Typography className="normal-text">
									Airman Certificates
								</Typography>
							</div>
						</div> */}
					</div>
					<div className="second-column">
						<Typography className="normal-text">
							Do you have a nickname or a 'callsign'? Is there a certain way to
							spell your name? (Optional)
						</Typography>
						<div className="item-separator-tiny" />
						<CommonInput
							control={control}
							name="nickName"
							placeholder="Call me..."
						/>
						<Typography className="normal-text item-separator-small">
							Languages you speak. (Optional)
						</Typography>
						<div className="item-separator-tiny" />
						<CommonInput
							control={control}
							name="languages"
							placeholder="Talk to me in..."
							prefix="Fluent:"
						/>
						<Typography className="normal-text item-separator-small">
							What aviation experience do you have? This can include what you've
							studied, your work, your hobbies, the airplanes you've flown, the
							years you've been flying, etc.
						</Typography>
						<div className="item-separator-tiny" />
						<CommonInput
							control={control}
							name="introduction"
							placeholder="My aviation life..."
							multiline
						/>
						<div className="save-profile-button-container item-separator-small">
							<Button
								variant="contained"
								onClick={onClickCancel}
								className="normal-button disabled-button no-transform">
								Cancel
							</Button>
							<Button
								variant="contained"
								className={classNames("normal-button no-transform", {
									"primary-button": !isDisabled,
									"disabled-button": isDisabled,
								})}
								type="submit">
								Save
							</Button>
						</div>
					</div>
				</form>
			</div>
		</div>
	);
}
